// What-if fork (§7 cross-branch thinking, made cheap): explore one option a
// few levels deep by taking the navigator's recommended default at each step.
// Speculative nodes are temp-registered so breadcrumbs see the chain, then
// discarded — the real graph never learns about them.

import { complete } from "../api/model";
import { parseModelJSON } from "../lib/json-repair";
import { tidyProse } from "../lib/text";
import { generateNode } from "./navigator";
import { comparePrompt } from "./prompts";
import { traceParseOutcome } from "./telemetry";
import { groundingLine, state, type DecisionNode, type Stakes } from "./state";

export interface FutureStep { question: string; take: string | undefined; stakes: Stakes }

export async function exploreFuture(
  fromNode: DecisionNode,
  optIdx: number,
  depth: number,
  onStep?: (step: FutureStep, d: number) => void,
): Promise<FutureStep[]> {
  const temps: string[] = [];
  try {
    let parent = fromNode, oi = optIdx;
    const steps: FutureStep[] = [];
    for (let d = 0; d < depth; d++) {
      const child = await generateNode({ parentNode: parent, optionIndex: oi });
      state.nodes.set(child.id, child);
      temps.push(child.id);
      const rec = child.recommend ?? 0;
      child.chosenIndex = rec;                 // the future assumes the default
      steps.push({ question: child.question, take: child.options[rec]?.label, stakes: child.stakes });
      onStep?.(steps[steps.length - 1]!, d);
      parent = child; oi = rec;
    }
    return steps;
  } finally { temps.forEach((id) => state.nodes.delete(id)); }
}

export interface FutureVerdict { pick: "A" | "B"; why: string; aRisk: string; bRisk: string }

export async function compareFutures(
  node: DecisionNode, aIdx: number, bIdx: number, aSteps: FutureStep[], bSteps: FutureStep[],
): Promise<FutureVerdict> {
  const fmt = (idx: number, steps: FutureStep[]) =>
    `${node.options[idx]!.label}\n${steps.map((s, i) => `  ${i + 1}. ${s.question} → ${s.take}`).join("\n")}`;
  const prompt = comparePrompt(
    { idea: state.idea, grounding: groundingLine("n/a"), facts: state.facts, constraints: state.constraints },
    node.question, fmt(aIdx, aSteps), fmt(bIdx, bSteps),
  );
  const raw = await complete(prompt, { max: 220, temp: 0.4, kind: "whatif.compare" });
  const { data, parse } = parseModelJSON(raw);
  traceParseOutcome("whatif.compare", parse, raw);
  if (!data) throw new Error("compare json unusable");
  return {
    pick: data.pick === "B" ? "B" : "A",
    why: tidyProse(String(data.why || "")),
    aRisk: tidyProse(String(data.aRisk || "")),
    bRisk: tidyProse(String(data.bRisk || "")),
  };
}
